/**
 * @file `useAnalytics` + `track` — dependency-agnostic Umami plumbing
 *       shared by every LabCraft SPA (www, lab, …).
 * @module @labcraft/ui/hooks/use-analytics
 *
 * The self-hosted Umami tracker is never baked into a static SPA
 * bundle. Its script URL + website ID arrive at runtime from each
 * daemon's `GET /api/config` (operator-set in the admin settings
 * panel), so analytics can be turned on/off and re-pointed without a
 * rebuild. {@link useAnalytics} appends the
 * `<script defer data-website-id>` tag to `<head>`
 * exactly once, and
 * only when both values are present — every surface ships
 * analytics-dark by default. By default Umami's tracker auto-records the
 * initial pageview and subsequent SPA navigations, so no per-route call
 * is needed; {@link track} is the typed escape hatch for custom funnel
 * events later.
 *
 * A surface that must control pageview timing passes
 * `{ autoTrack: false }` and drives pageviews itself via
 * {@link trackPageview}. The lab does this so it can `identify()` a known
 * learner *before* the first pageview — with auto-track on, the tracker
 * fires that pageview during its own script execution, before any code
 * can identify, opening an anonymous "shadow" session for every
 * signed-in learner. www keeps the default (it never identifies, so it
 * has no shadow).
 *
 * This module stays **React-only for its DOM concerns**: the
 * `<script>` injection and the `window.umami` calls import `react` and
 * touch no app client — it does NOT reach for `@tanstack/react-query`, a
 * per-app `useConfig`, or any app client, so the one copy stays shareable
 * across www, lab, and any future surface. The analytics *logic* it can't
 * hand-roll here — the scalar-only session-value typing — is delegated to
 * `@labcraft/core`'s `CoreAnalytics` (the single home for analytics logic),
 * so the PII floor lives in one place. The `SessionData` / `SessionValue`
 * types are re-exported from here so existing consumers keep importing them
 * from `@labcraft/ui/hooks` unchanged.
 */

import { useEffect } from 'react'
import type { SessionData, SessionValue } from '@labcraft/core'

export type { SessionData, SessionValue }

declare global {
  /**
   * Augments the DOM `Window` with the global Umami injects once its
   * tracker script loads. Optional because the tracker is absent until
   * the operator configures analytics and the script finishes loading.
   *
   * @property umami - Umami's runtime tracker, present once the
   *   injected `<script>` has loaded. `track(event, data?)` records a
   *   custom event; `data` values are coarse scalars by construction so
   *   no PII-shaped payload can be sent by accident. The
   *   `track(payload => …)` overload is the tracker's payload-transform
   *   form: the callback receives the default pageview payload (website,
   *   screen, referrer, the tracker's cached `url`, …) and returns a
   *   modified copy — {@link trackPageview} uses it to override the stale
   *   cached `url` with the live location (see its note).
   */
  interface Window {
    umami?: {
      track(
        event?: string,
        data?: Record<string, string | number | boolean>,
      ): void
      track(
        payload: (props: Record<string, unknown>) => Record<string, unknown>,
      ): void
      identify(
        distinctId: string,
        data?: Record<string, string | number | boolean>,
      ): void
    }
  }
}

/**
 * Resolved Umami config the SPA injects when both values are present.
 * Each app resolves this from its own runtime-config fetch and passes
 * it (or `null` when analytics is off) to {@link useAnalytics}.
 *
 * @property scriptUrl - Full URL of the self-hosted Umami tracker
 *   script (e.g. `https://analytics.labcraft.dev/script.js`).
 * @property websiteId - Umami website ID (UUID) for this surface,
 *   created in the Umami dashboard. www and lab use distinct IDs.
 */
export type AnalyticsConfig = {
  scriptUrl: string
  websiteId: string
}

/**
 * Options for {@link useAnalytics}.
 *
 * @property autoTrack - Whether Umami records the initial pageview and
 *   subsequent SPA navigations itself (`data-auto-track`). Defaults to
 *   `true` (the tracker's own default). Pass `false` to take manual
 *   control of pageview timing — the caller must then drive pageviews via
 *   {@link trackPageview}. Lab passes `false` so `identify()` can precede
 *   the first pageview (see the module header's shadow-session note).
 */
export type UseAnalyticsOptions = {
  autoTrack?: boolean
}

/**
 * Load the Umami tracker once when `config` is non-null. No-op while
 * `config` is `null` (analytics-dark default); idempotent across
 * re-renders and StrictMode double-invocation (guards on an
 * already-present tag keyed by `data-website-id`). Pure React effect —
 * the only `@labcraft/ui` dependency it carries is `react`.
 *
 * @param config - Resolved analytics config, or `null` when the
 *   operator has not set both the script URL and website ID. The
 *   calling app supplies this from its own runtime-config read.
 * @param options - Optional behavior flags; see {@link UseAnalyticsOptions}.
 *   Omitted defaults keep the tracker's stock behavior (auto-track on), so
 *   existing callers are unaffected.
 */
export function useAnalytics(
  config: AnalyticsConfig | null,
  options: UseAnalyticsOptions = {},
): void {
  const scriptUrl = config?.scriptUrl
  const websiteId = config?.websiteId
  const autoTrack = options.autoTrack ?? true

  useEffect(() => {
    if (!scriptUrl || !websiteId) return
    if (document.querySelector(`script[data-website-id="${websiteId}"]`)) return
    const el = document.createElement('script')
    el.defer = true
    el.src = scriptUrl
    el.setAttribute('data-website-id', websiteId)
    // Disable the tracker's own initial-pageview + SPA-route recording when
    // the caller drives pageviews manually (lab, so identify precedes the
    // first pageview). Absent attribute = Umami's default (auto-track on).
    if (!autoTrack) el.setAttribute('data-auto-track', 'false')
    // Do NOT make the tracker honor the browser DNT signal: our Umami is
    // first-party product analytics (cookieless; on lab, behind a consented
    // signup), so a cross-site-tracking preference doesn't apply. Honoring it
    // silently blinds our own product telemetry (PR #1520 — never re-add).
    document.head.appendChild(el)
  }, [scriptUrl, websiteId, autoTrack])
}

/**
 * Typed wrapper over `window.umami?.track` for custom funnel events.
 * No-op when the tracker is absent (analytics-dark, or the script has
 * not loaded yet), so call sites never need to guard. `data` values are
 * limited to `string | number | boolean` — coarse and PII-free by
 * construction; richer payloads have no place in product analytics.
 *
 * @param event - Custom event name (e.g. `'signup_clicked'`).
 * @param data - Optional flat scalar payload describing the event.
 */
export function track(
  event: string,
  data?: Record<string, string | number | boolean>,
): void {
  window.umami?.track(event, data)
}

/**
 * Record a pageview for the **current** URL. No-op when the tracker is
 * absent (analytics-dark, or the script has not loaded yet), so call sites
 * never need to guard.
 *
 * Only needed when auto-track is disabled (`useAnalytics(config, {
 * autoTrack: false })`); with auto-track on, the tracker sends pageviews
 * itself. The lab uses this to control ordering — `identify()` before the
 * first pageview — so a known learner never opens an anonymous shadow
 * session (see the module header).
 *
 * **Why it passes `url` explicitly and does NOT call `track()` bare.** With
 * auto-track off, Umami never installs its `history.pushState` /
 * `replaceState` hooks — those hooks are the only thing that refreshes the
 * tracker's cached URL on SPA navigation. So the cached URL freezes at the
 * value captured when the script first loaded (the entry/login page), and a
 * bare `umami.track()` would report that one stale URL for every route — no
 * pageview past the entry page ever lands (the #1552 regression). The
 * payload-transform form overrides `url` with the live `location.href`,
 * which react-router has already updated by the time the pageview effect
 * runs, so each route change records its real URL.
 */
export function trackPageview(): void {
  window.umami?.track(props => ({ ...props, url: window.location.href }))
}

/**
 * Typed wrapper over `window.umami?.identify` — the once-per-session
 * identity call that turns an anonymous Umami session into a known
 * learner, unlocking cohort / retention / segmentation on a login-only
 * product (umbrella #1213, Phase 2). No-op when the tracker is absent
 * (analytics-dark, or the script has not loaded yet), so call sites never
 * need to guard on `window.umami`.
 *
 * Enforces the umbrella's PII invariant at this boundary:
 *
 *   - `distinctId` MUST be the learner's `learners.id` UUID. The sole
 *     caller passes `learner.id`, which is already `z.string().uuid()`-
 *     validated by `LearnerSchema` upstream, so no wrapper-side re-check is
 *     needed — a domain-wide `LearnerId` brand that would make this a
 *     type-level guarantee is tracked in #1241.
 *   - `data` is a {@link SessionData}: a closed record of {@link
 *     SessionValue} scalars. The caller supplies a fully-bounded closed
 *     type (`{ plan; cohortWeek; acquisitionSource; acquisitionCategory }`),
 *     so no email / name / raw-referrer key can be passed and no non-scalar
 *     value type-checks.
 *
 * Idempotency is the caller's concern (identify must fire once per
 * session, not once per render) — see the lab SPA's `useIdentify` guard.
 * This wrapper is a thin, stateless forward; calling it twice with the
 * same distinct ID is harmless (Umami collapses same-ID identifies into
 * one profile), but re-forwarding on every render is wasteful, which is
 * why the guard lives at the call site.
 *
 * @param distinctId - The learner's `learners.id` UUID.
 * @param data - Optional closed session-property record (all scalars).
 */
export function identify(distinctId: string, data?: SessionData): void {
  window.umami?.identify(distinctId, data)
}
